<template>
  <div class="pc-catalog-wrap" @click.stop>
    <a href="javascript:;" class="close-panel" aria-label="关闭目录" @click="$emit('close')"><nricon name="close" /></a>
    <div class="panel-box">
      <!-- 标签行：目录 tab（红字红下划线）+ 搜索框（保留原有搜索能力） -->
      <div class="catalog-head">
        <span class="catalog-tab">目录</span>
        <div class="ft-search">
          <nricon name="search" />
          <input
            v-model="searchText"
            class="ft-input"
            placeholder="搜索章节标题，回车全文搜索"
            @keyup.enter="fullTextSearch"
          >
          <button class="ft-btn" @click="fullTextSearch">
            <nricon v-if="searching" name="loading" class="spin" />
            <span v-else>全文</span>
          </button>
        </div>
      </div>

      <!-- 全文搜索结果 -->
      <div v-if="mode === 'fulltext'" class="catalog-scroll ft-mode">
        <div class="ft-hint">
          命中 {{ fullResults.length }} 章
          <button class="ft-back" @click="exitFullText">返回目录</button>
        </div>
        <button
          v-for="r in fullResults"
          :key="r.chapterId"
          class="ft-item"
          :class="{ on: currentChapterId === r.chapterId }"
          @click="selectById(r.chapterId)"
        >
          <div class="ft-title">{{ r.title }}</div>
          <div class="ft-snippet" v-html="highlight(r.snippet)"></div>
        </button>
        <div v-if="!fullResults.length" class="ft-empty">未找到匹配内容</div>
      </div>

      <!-- 目录：两列虚拟滚动，支持上万章 -->
      <virtual-list v-else ref="vlist" class="catalog-scroll" :items="filteredChapters" :item-height="40" :columns="2" :gap="5">
        <template #default="{ item }">
          <a href="javascript:;" class="catalog-item" :class="{ on: currentChapterId === item.id }" :title="item.title" @click="select(item)">{{ item.title }}</a>
        </template>
      </virtual-list>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import VirtualList from '@/components/VirtualList.vue'

export default {
  name: 'ChapterCatalog',
  components: { VirtualList },
  props: {
    chapters: { type: Array, default: () => [] },
    currentChapterId: { type: [String, Number], default: null },
    bookId: { type: [String, Number], default: null }
  },
  data() {
    return {
      searchText: '',
      mode: 'toc', // toc | fulltext
      fullResults: [],
      searching: false
    }
  },
  computed: {
    filteredChapters() {
      if (!this.searchText) return this.chapters
      const kw = this.searchText.toLowerCase()
      return this.chapters.filter(c => c.title.toLowerCase().includes(kw))
    }
  },
  mounted() {
    this.$nextTick(this.scrollToActive)
  },
  watch: {
    currentChapterId() { this.$nextTick(this.scrollToActive) },
    chapters() { this.$nextTick(this.scrollToActive) },
    searchText() { this.$nextTick(this.scrollToActive) }
  },
  methods: {
    select(chapter) {
      this.$emit('select', chapter, true)
      this.$emit('close')
    },
    async fullTextSearch() {
      const kw = this.searchText.trim()
      if (!kw) {
        this.exitFullText()
        return
      }
      if (!this.bookId) return
      this.searching = true
      try {
        const { data } = await api.book.searchInBook(this.bookId, kw)
        this.fullResults = data
        this.mode = 'fulltext'
      } catch (e) {
        this.$message.error('全文搜索失败')
      } finally {
        this.searching = false
      }
    },
    exitFullText() {
      this.mode = 'toc'
      this.fullResults = []
    },
    selectById(chapterId) {
      const chapter = this.chapters.find(c => c.id === chapterId)
      if (chapter) {
        // 带上搜索词，阅读器据此在正文里高亮命中并滚动定位
        this.$emit('select', chapter, true, this.searchText.trim())
        this.$emit('close')
      }
    },
    // 转义后高亮关键词，供 v-html 使用（避免 XSS）
    highlight(text) {
      if (!text) return ''
      const esc = s => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
      const safe = esc(text)
      const kw = this.searchText.trim()
      if (!kw) return safe
      const kwEsc = esc(kw).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return safe.replace(new RegExp(kwEsc, 'gi'), m => `<mark>${m}</mark>`)
    },
    scrollToActive() {
      if (this.mode !== 'toc') return
      const idx = this.filteredChapters.findIndex(c => c.id === this.currentChapterId)
      if (idx < 0) return
      const vlist = this.$refs.vlist
      if (vlist && vlist.scrollToIndex) vlist.scrollToIndex(idx, 'center')
    }
  }
}
</script>

<style lang="scss" scoped>
/* PC 目录面板：复刻参考站 .panel-wrap.catalog（798 宽浮层，挂目录按钮右侧） */
.pc-catalog-wrap {
  position: absolute;
  top: -1px;
  left: 69px;
  z-index: 120;
  width: 798px;
  max-width: calc(100vw - 200px);
  background: #fdfdfc url('/reader-skins/base_theme_popup.png') repeat;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: rgba(0, 0, 0, 0.2) 0 5px 20px;
  box-sizing: border-box;

  .close-panel {
    position: absolute;
    top: 8px;
    right: 8px;
    display: grid;
    width: 24px;
    height: 24px;
    place-items: center;
    color: #a6a6a6;
    font-size: 24px;

    &:hover { color: #666; }
  }

  .panel-box {
    padding: 40px 0 40px 24px;
    box-sizing: border-box;
  }

  // 标签行：tab + 搜索框
  .catalog-head {
    display: flex;
    align-items: center;
    margin: 0 24px 24px 0;
  }

  .catalog-tab {
    flex: none;
    padding: 0 2px 4px;
    color: #ed4259;
    font-size: 18px;
    line-height: 22px;
    border-bottom: 1px solid #ed4259;
  }

  .ft-search {
    display: flex;
    flex: 1;
    min-width: 0;
    max-width: 300px;
    height: 32px;
    align-items: center;
    gap: 6px;
    margin-left: auto;
    padding: 0 4px 0 10px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    background: rgba(255, 255, 255, 0.6);
    box-sizing: border-box;

    &:focus-within { border-color: #ed4259; }

    > i { flex: none; color: #a6a6a6; font-size: 14px; }

    .ft-input {
      flex: 1;
      min-width: 0;
      border: 0;
      outline: none;
      background: transparent;
      color: #262626;
      font-size: 13px;

      &::placeholder { color: #a6a6a6; }
    }

    .ft-btn {
      flex: none;
      display: flex;
      height: 24px;
      padding: 0 8px;
      align-items: center;
      border: 0;
      background: transparent;
      color: #666;
      font-size: 12px;
      cursor: pointer;

      &:hover { color: #ed4259; }
      .spin { animation: catalog-spin 0.8s linear infinite; }
    }
  }

  // 滚动区：max-height 对齐参考站（视口 - 250）
  .catalog-scroll {
    max-height: calc(100vh - 250px);
    overflow-y: auto;
    padding-right: 24px;
    box-sizing: border-box;
  }

  // 目录项：两列 40 高（VirtualList columns=2 网格）
  .catalog-item {
    display: block;
    height: 40px;
    overflow: hidden;
    color: #262626;
    font-size: 16px;
    line-height: 40px;
    text-decoration: none;
    text-overflow: ellipsis;
    white-space: nowrap;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    box-sizing: border-box;
    transition: color 0.15s ease;

    &:hover { color: #ed4259; }

    &.on { color: #ed4259; }
  }

  // 全文搜索结果
  .ft-mode { padding-right: 24px; }

  .ft-hint {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 2px 0 10px;
    color: #a6a6a6;
    font-size: 12px;

    .ft-back {
      padding: 0 6px;
      border: 0;
      background: transparent;
      color: #ed4259;
      font-size: 12px;
      cursor: pointer;

      &:hover { text-decoration: underline; }
    }
  }

  .ft-item {
    display: block;
    width: 100%;
    padding: 10px 0;
    border: 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    background: transparent;
    text-align: left;
    cursor: pointer;

    &.on .ft-title { color: #ed4259; }
  }

  .ft-title {
    color: #262626;
    font-size: 15px;
    line-height: 1.4;
  }

  .ft-snippet {
    margin-top: 3px;
    color: #a6a6a6;
    font-size: 12px;
    line-height: 1.5;

    ::v-deep mark {
      padding: 0 1px;
      border-radius: 2px;
      background: #fff3a3;
      color: inherit;
    }
  }

  .ft-empty {
    padding: 24px 0;
    color: #a6a6a6;
    font-size: 13px;
    text-align: center;
  }
}

@keyframes catalog-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 面板纹理随主题联动（与设置面板同源：theme_X_popup_bg.png） */
.theme-eye .pc-catalog-wrap { background: #f3fbf3 url('/reader-skins/theme_2_popup_bg.png') repeat; }
.theme-sepia .pc-catalog-wrap { background: #f8f1d9 url('/reader-skins/theme_1_popup_bg.png') repeat; }
.theme-blue .pc-catalog-wrap { background: #effafd url('/reader-skins/theme_3_popup_bg.png') repeat; }
.theme-pink .pc-catalog-wrap { background: #faeceb; }
.theme-grey .pc-catalog-wrap { background: #ececec url('/reader-skins/theme_5_popup_bg.png') repeat; }

// 深色主题：摘浅色纹理，转深色配色
.theme-dark .pc-catalog-wrap,
.theme-black .pc-catalog-wrap {
  background-image: none;
  background-color: #1e1e1e;
  border-color: rgba(255, 255, 255, 0.12);

  .catalog-item { color: rgba(255, 255, 255, 0.75); border-bottom-color: rgba(255, 255, 255, 0.08); }
  .ft-search { border-color: rgba(255, 255, 255, 0.15); background: rgba(255, 255, 255, 0.04); }
  .ft-search .ft-input { color: rgba(255, 255, 255, 0.8); }
  .ft-search > i,
  .ft-search .ft-btn { color: rgba(255, 255, 255, 0.5); }
  .ft-title { color: rgba(255, 255, 255, 0.8); }
  .ft-snippet { color: rgba(255, 255, 255, 0.4); }
  .ft-item { border-bottom-color: rgba(255, 255, 255, 0.08); }
}
</style>
